import fetch from "cross-fetch";
import { useCallback, useState, useMemo, useEffect } from "react";
import { useRouter } from "next/router";
import { mutate } from "swr";
import useContacts from "@/components/context/useContacts";
import { Contact, ContactChannel, contactTo } from "@/components/models/Contact";
import { Button } from "@/components/ui";

import { PageHeader } from "@/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/Badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Input } from "@/components/ui/Input";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  MoreHorizontal,
  Pencil,
  Trash,
  Play,
  RotateCcw,
  PlusIcon,
  SmartphoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserPlusIcon,
  Search,
} from "lucide-react";

export default function ContactsList() {
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const { contacts, pagination, isLoading, isError } = useContacts(
    currentPage,
    perPage,
    debouncedSearch,
  );

  const [testingContact, setTestingContact] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{
    contactId: string;
    success: boolean;
    message: string;
  } | null>(null);
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "error">("all");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1); // Reset to first page on search
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const onEditContact = useCallback(
    (contact: Contact) => {
      router.push(`/dashboard/contacts/${contact.id}/edit`);
    },
    [router],
  );

  const addContactClicked = useCallback(() => {
    router.push("/dashboard/contacts/new");
  }, [router]);

  const deleteClicked = useCallback(
    (contact: Contact) => {
      if (confirm("Are you sure? This contact will be deleted from all monitors.")) {
        fetch(`/web_api/contacts/${contact.id}`, {
          method: "DELETE",
        })
          .then((resp) => resp.json())
          .then((json) => {
            // console.log({ json });

            if (json.status === "ok") {
              const queryParams = new URLSearchParams({
                page: currentPage.toString(),
                per_page: perPage.toString(),
              });
              if (debouncedSearch) {
                queryParams.append("search", debouncedSearch);
              }
              mutate(`/web_api/contacts?${queryParams}`);
            }
          })
          .catch((e) => {
            console.error(e);
          });
      }
    },
    [currentPage, perPage, debouncedSearch],
  );

  const reactivateContactClicked = useCallback(
    (contact: Contact) => {
      // ask user if they fixed any issues with this contact
      if (
        confirm("Are you sure? This contact will be reactivated and start receiving alerts again.")
      ) {
        fetch(`/web_api/contacts/${contact.id}/reactivate`, {
          method: "POST",
        })
          .then((resp) => resp.json())
          .then((json) => {
            // console.log({ json });

            if (json.status === "ok") {
              const queryParams = new URLSearchParams({
                page: currentPage.toString(),
                per_page: perPage.toString(),
              });
              if (debouncedSearch) {
                queryParams.append("search", debouncedSearch);
              }
              mutate(`/web_api/contacts?${queryParams}`);
            }
          })
          .catch((e) => {
            console.error(e);
          });
      }
    },
    [currentPage, perPage, debouncedSearch],
  );

  const testContactClicked = useCallback((contact: Contact) => {
    setTestingContact(contact.id);
    setTestResult(null);

    fetch(`/web_api/contacts/${contact.id}/test`, {
      method: "POST",
    })
      .then((resp) => resp.json())
      .then((json) => {
        if (json.status === "ok") {
          setTestResult({
            contactId: contact.id,
            success: true,
            message: json.message || "Test notification sent successfully!",
          });
        } else {
          setTestResult({
            contactId: contact.id,
            success: false,
            message: json.error || "Failed to send test notification",
          });
        }
      })
      .catch((e) => {
        console.error(e);
        setTestResult({
          contactId: contact.id,
          success: false,
          message: "Network error: Failed to send test notification",
        });
      })
      .finally(() => {
        setTestingContact(null);
        // Clear the result after 5 seconds
        setTimeout(() => {
          setTestResult(null);
        }, 5000);
      });
  }, []);

  const filterByStatus = useCallback(
    (contact: Contact): boolean => {
      if (statusFilter === "all") {
        return true;
      }
      if (statusFilter === "active") {
        return contact.active === true;
      }
      if (statusFilter === "error") {
        return contact.active === false;
      }
      return true;
    },
    [statusFilter],
  );

  const filteredContacts = useMemo(() => {
    return contacts.filter(filterByStatus);
  }, [contacts, filterByStatus]);

  const [activeCount, errorCount] = useMemo(() => {
    const active = contacts.filter((c) => c.active === true).length;
    const error = contacts.filter((c) => c.active === false).length;
    return [active, error];
  }, [contacts]);

  const getContactTypeLabel = useCallback((contact: Contact) => {
    if (contact.channel === "apns") {
      return "iOS Push";
    }
    // ContactChannel is an object type with different fields
    const channel = contact.channel as ContactChannel;
    if (channel.email) return "Email";
    if (channel.sms) return "SMS";
    if (channel.webhook) return "Webhook";
    if (channel.slack) return "Slack";
    if (channel.discord) return "Discord";
    if (channel.pagerduty) return "PagerDuty";
    if (channel.incidentio) return "incident.io";
    if (channel.opsgenie) return "Opsgenie";
    if (channel.zendesk) return "Zendesk";
    if (channel.teams) return "Microsoft Teams";
    return "Unknown";
  }, []);

  const pageTitle = useMemo(() => {
    if (contacts.length === 0) {
      return "Alert Contacts";
    }
    if (errorCount > 0) {
      return "Some contacts have errors";
    }
    return "All contacts operational";
  }, [contacts.length, errorCount]);

  const pageDescription = useMemo(() => {
    if (contacts.length === 0) {
      if (debouncedSearch) {
        return `No contacts found for "${debouncedSearch}"`;
      }
      return "Add your first contact to start receiving alerts";
    }
    if (errorCount > 0) {
      return `${errorCount} ${errorCount === 1 ? "contact has" : "contacts have"} errors`;
    }
    const totalText = pagination ? `${pagination.total} total` : "";
    return `${contacts.length} ${contacts.length === 1 ? "contact" : "contacts"} configured${totalText ? ` (${totalText})` : ""}`;
  }, [contacts.length, errorCount, debouncedSearch, pagination]);

  return (
    <div className="space-y-6">
      <PageHeader title={pageTitle} description={pageDescription}>
        <div className="flex items-center gap-2">
          {errorCount > 0 && <div className="pulse-red w-6 h-6 sm:w-8 sm:h-8" />}
          {errorCount === 0 && contacts.length > 0 && (
            <div className="pulse-lime w-6 h-6 sm:w-8 sm:h-8" />
          )}
          <div className="flex flex-col gap-2 w-full sm:w-auto">
            <Button onClick={addContactClicked} className="w-full sm:w-auto">
              <PlusIcon className="h-4 w-4 mr-2" />
              New Contact
            </Button>
            <a
              href="https://apps.apple.com/us/app/uptime-monitor-notifier/id6450390377"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                size="sm"
                className="whitespace-nowrap w-full sm:w-auto text-xs sm:text-sm cursor-pointer"
              >
                <SmartphoneIcon className="h-4 w-4 mr-2" />
                Mobile App
              </Button>
            </a>
          </div>
        </div>
      </PageHeader>

      {/* Error State */}
      {isError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 mb-6">
          <p className="text-sm text-destructive">
            Failed to load contacts. Please try refreshing the page.
          </p>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search contacts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <ToggleGroup
          type="single"
          value={statusFilter}
          onValueChange={(value) => value && setStatusFilter(value as any)}
          className="justify-start grid grid-cols-3 sm:flex w-full sm:w-auto"
        >
          <ToggleGroupItem value="all" variant="outline" className="text-xs sm:text-sm">
            All
            <Badge variant="secondary" className="ml-1 sm:ml-2 text-xs">
              {pagination?.total || contacts.length}
            </Badge>
          </ToggleGroupItem>
          <ToggleGroupItem value="active" variant="outline" className="text-xs sm:text-sm">
            Active
            {activeCount > 0 && (
              <Badge variant="success" className="ml-1 sm:ml-2 text-xs">
                {activeCount}
              </Badge>
            )}
          </ToggleGroupItem>
          <ToggleGroupItem value="error" variant="outline" className="text-xs sm:text-sm">
            Error
            {errorCount > 0 && (
              <Badge variant="destructive" className="ml-1 sm:ml-2 text-xs">
                {errorCount}
              </Badge>
            )}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* Contacts Table - Desktop */}
      <div className="hidden sm:block rounded-md border">
        {filteredContacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="rounded-full bg-muted p-3 mb-4">
              <UserPlusIcon className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-1">
              {debouncedSearch
                ? "No contacts found"
                : statusFilter === "all"
                  ? "No contacts yet"
                  : `No ${statusFilter} contacts`}
            </h3>
            <p className="text-muted-foreground text-center max-w-sm">
              {debouncedSearch
                ? `No contacts match "${debouncedSearch}". Try a different search term.`
                : statusFilter === "all"
                  ? "Get started by adding your first contact to receive uptime alerts."
                  : `You don't have any contacts that are currently ${statusFilter}.`}
            </p>
            {!debouncedSearch && statusFilter === "all" && contacts.length === 0 && (
              <Button onClick={addContactClicked} className="mt-4">
                <PlusIcon className="h-4 w-4 mr-2" />
                Add your first contact
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Destination</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell className="font-medium">{contact.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{getContactTypeLabel(contact)}</Badge>
                  </TableCell>
                  <TableCell className="max-w-[300px]">
                    <span
                      className="text-muted-foreground text-sm block truncate"
                      title={contactTo(contact)}
                    >
                      {contactTo(contact)}
                    </span>
                  </TableCell>
                  <TableCell>
                    {contact.active ? (
                      <div className="flex items-center gap-2">
                        <CheckCircleIcon className="h-4 w-4 text-green-500" />
                        <span className="text-sm">Active</span>
                        {testResult && testResult.contactId === contact.id && (
                          <Badge
                            variant={testResult.success ? "success" : "destructive"}
                            className="text-xs"
                          >
                            {testResult.success ? "Test sent" : "Test failed"}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <XCircleIcon className="h-4 w-4 text-destructive" />
                        <span className="text-sm text-destructive">{contact.error || "Error"}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[160px]">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {contact.active && (
                          <DropdownMenuItem
                            onClick={() => testContactClicked(contact)}
                            disabled={testingContact === contact.id}
                          >
                            <Play className="mr-2 h-4 w-4" />
                            {testingContact === contact.id ? "Testing..." : "Test contact"}
                          </DropdownMenuItem>
                        )}
                        {!contact.active && (
                          <DropdownMenuItem onClick={() => reactivateContactClicked(contact)}>
                            <RotateCcw className="mr-2 h-4 w-4" />
                            Reactivate
                          </DropdownMenuItem>
                        )}
                        {contact.channel !== "apns" && (
                          <DropdownMenuItem onClick={() => onEditContact(contact)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit contact
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => deleteClicked(contact)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash className="mr-2 h-4 w-4" />
                          Delete contact
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Contacts Cards - Mobile */}
      <div className="sm:hidden space-y-3">
        {filteredContacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 rounded-md border">
            <div className="rounded-full bg-muted p-3 mb-3">
              <UserPlusIcon className="h-5 w-5 text-muted-foreground" />
            </div>
            <h3 className="text-base font-medium mb-1 text-center">
              {debouncedSearch
                ? "No contacts found"
                : statusFilter === "all"
                  ? "No contacts yet"
                  : `No ${statusFilter} contacts`}
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              {debouncedSearch
                ? `No contacts match "${debouncedSearch}".`
                : statusFilter === "all"
                  ? "Add your first contact to receive alerts."
                  : `No ${statusFilter} contacts found.`}
            </p>
            {!debouncedSearch && statusFilter === "all" && contacts.length === 0 && (
              <Button onClick={addContactClicked} className="mt-4 w-full" size="sm">
                <PlusIcon className="h-4 w-4 mr-2" />
                Add your first contact
              </Button>
            )}
          </div>
        ) : (
          filteredContacts.map((contact) => (
            <div key={contact.id} className="rounded-md border p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium truncate">{contact.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">
                      {getContactTypeLabel(contact)}
                    </Badge>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                      <MoreHorizontal className="h-4 w-4" />
                      <span className="sr-only">Open menu</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-[160px]">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {contact.active && (
                      <DropdownMenuItem
                        onClick={() => testContactClicked(contact)}
                        disabled={testingContact === contact.id}
                      >
                        <Play className="mr-2 h-4 w-4" />
                        {testingContact === contact.id ? "Testing..." : "Test contact"}
                      </DropdownMenuItem>
                    )}
                    {!contact.active && (
                      <DropdownMenuItem onClick={() => reactivateContactClicked(contact)}>
                        <RotateCcw className="mr-2 h-4 w-4" />
                        Reactivate
                      </DropdownMenuItem>
                    )}
                    {contact.channel !== "apns" && (
                      <DropdownMenuItem onClick={() => onEditContact(contact)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit contact
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => deleteClicked(contact)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash className="mr-2 h-4 w-4" />
                      Delete contact
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">Destination</div>
                <div className="text-sm truncate" title={contactTo(contact)}>
                  {contactTo(contact)}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {contact.active ? (
                  <>
                    <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm">Active</span>
                    {testResult && testResult.contactId === contact.id && (
                      <Badge
                        variant={testResult.success ? "success" : "destructive"}
                        className="text-xs"
                      >
                        {testResult.success ? "Test sent" : "Test failed"}
                      </Badge>
                    )}
                  </>
                ) : (
                  <>
                    <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
                    <span className="text-sm text-destructive truncate">
                      {contact.error || "Error"}
                    </span>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground whitespace-nowrap">
            Showing {(currentPage - 1) * perPage + 1} to{" "}
            {Math.min(currentPage * perPage, pagination.total)} of {pagination.total} contacts
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  className={
                    currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"
                  }
                />
              </PaginationItem>

              {/* Show all pages if total pages <= 7 */}
              {pagination.total_pages <= 7 ? (
                Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((page) => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      onClick={() => setCurrentPage(page)}
                      isActive={currentPage === page}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))
              ) : (
                <>
                  {/* First page */}
                  {currentPage > 3 && (
                    <>
                      <PaginationItem>
                        <PaginationLink
                          onClick={() => setCurrentPage(1)}
                          className="cursor-pointer"
                        >
                          1
                        </PaginationLink>
                      </PaginationItem>
                      {currentPage > 4 && (
                        <PaginationItem>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )}
                    </>
                  )}

                  {/* Pages around current */}
                  {Array.from({ length: 5 }, (_, i) => {
                    const page = currentPage - 2 + i;
                    if (page < 1 || page > pagination.total_pages) return null;
                    return (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => setCurrentPage(page)}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  }).filter(Boolean)}

                  {/* Last page */}
                  {currentPage < pagination.total_pages - 2 && (
                    <>
                      {currentPage < pagination.total_pages - 3 && (
                        <PaginationItem>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )}
                      <PaginationItem>
                        <PaginationLink
                          onClick={() => setCurrentPage(pagination.total_pages)}
                          className="cursor-pointer"
                        >
                          {pagination.total_pages}
                        </PaginationLink>
                      </PaginationItem>
                    </>
                  )}
                </>
              )}

              <PaginationItem>
                <PaginationNext
                  onClick={() => setCurrentPage(Math.min(pagination.total_pages, currentPage + 1))}
                  className={
                    currentPage === pagination.total_pages
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
